import { ExpenseBankSelect } from "@/components/expenses/ExpenseBankSelect";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { paymentMethodMeta } from "@/lib/expenses/expensesStore";
import type { BankOption } from "@/lib/sales/banksStore";
import type { SalePaymentMethod } from "@/lib/sales/salesStore";
import { cn } from "@/lib/utils";

export type PaymentMethodValue = SalePaymentMethod | "";

export type PaymentMethodDraft = {
  paymentMethod: PaymentMethodValue;
  bankId: string;
  transactionReferenceNo: string;
  payOrderNo: string;
  chequeNo?: string;
  depositSlipNo?: string;
  chequeDate?: string;
};

export function paymentMethodUsesBank(method: PaymentMethodValue): boolean {
  return method === "online" || method === "cheque" || method === "pay_order" || method === "cash_deposit";
}

export function emptyPaymentMethodDraft(
  patch?: Partial<PaymentMethodDraft>,
): PaymentMethodDraft {
  return {
    paymentMethod: "",
    bankId: "",
    transactionReferenceNo: "",
    payOrderNo: "",
    chequeNo: "",
    depositSlipNo: "",
    chequeDate: "",
    ...patch,
  };
}

/** Resolve the primary reference field for API payloads. */
export function paymentReferenceForSubmit(draft: PaymentMethodDraft): string | null {
  const method = draft.paymentMethod;
  if (method === "cheque") return draft.chequeNo?.trim() || null;
  if (method === "pay_order") return draft.payOrderNo.trim() || null;
  if (method === "cash_deposit") return draft.depositSlipNo?.trim() || draft.transactionReferenceNo.trim() || null;
  if (method === "online") return draft.transactionReferenceNo.trim() || null;
  return null;
}

export type LoanPaymentDraftLike = {
  paymentMethod: string;
  bankId: string;
  referenceNo: string;
  chequeNo: string;
  chequeDate: string;
};

export function loanDraftToPaymentDraft(value: LoanPaymentDraftLike): PaymentMethodDraft {
  const method = (value.paymentMethod || "") as PaymentMethodValue;
  return emptyPaymentMethodDraft({
    paymentMethod: method,
    bankId: value.bankId ?? "",
    transactionReferenceNo: method === "online" ? value.referenceNo ?? "" : "",
    payOrderNo: method === "pay_order" ? value.referenceNo ?? "" : "",
    depositSlipNo: method === "cash_deposit" ? value.referenceNo ?? "" : "",
    chequeNo: value.chequeNo ?? "",
    chequeDate: value.chequeDate ?? "",
  });
}

export function paymentDraftToLoanDraft(value: PaymentMethodDraft): LoanPaymentDraftLike {
  const method = value.paymentMethod;
  let referenceNo = "";
  if (method === "online") referenceNo = value.transactionReferenceNo;
  else if (method === "pay_order") referenceNo = value.payOrderNo;
  else if (method === "cash_deposit") referenceNo = value.depositSlipNo ?? value.transactionReferenceNo;

  return {
    paymentMethod: method,
    bankId: value.bankId,
    referenceNo,
    chequeNo: value.chequeNo ?? "",
    chequeDate: value.chequeDate ?? "",
  };
}

export type ExpensePaymentDraftLike = {
  paymentMethod: string;
  bankId: string;
  referenceNo: string;
  chequeNo: string;
  chequeDate: string;
};

export function expenseDraftToPaymentDraft(value: ExpensePaymentDraftLike): PaymentMethodDraft {
  return loanDraftToPaymentDraft(value);
}

export function paymentDraftToExpenseDraft(value: PaymentMethodDraft): ExpensePaymentDraftLike {
  return paymentDraftToLoanDraft(value);
}

/** Same clear rules as Create Sale Settlement when switching methods. */
export function applyPaymentMethodChange(
  current: PaymentMethodDraft,
  method: PaymentMethodValue,
): PaymentMethodDraft {
  const usesBank = method === "online" || method === "cheque" || method === "pay_order" || method === "cash_deposit";
  return {
    ...current,
    paymentMethod: method,
    bankId: usesBank ? current.bankId : "",
    transactionReferenceNo: method === "online" ? current.transactionReferenceNo : "",
    chequeNo: method === "cheque" ? (current.chequeNo ?? "") : "",
    payOrderNo: method === "pay_order" ? current.payOrderNo : "",
    depositSlipNo: method === "cash_deposit" ? (current.depositSlipNo ?? "") : "",
    chequeDate: method === "cheque" ? (current.chequeDate ?? "") : "",
  };
}

type SelectProps = {
  value: PaymentMethodValue;
  onChange: (method: PaymentMethodValue) => void;
  disabled?: boolean;
  placeholder?: string;
  noneLabel?: string;
  /** When false, hides the "none" entry — the first real method acts as the default. */
  allowNone?: boolean;
  id?: string;
  label?: string;
  required?: boolean;
  labelClassName?: string;
  triggerClassName?: string;
  className?: string;
};

/** Cash / Online / Cheque / Pay Order / Cash Deposit selector used by Settlement and product cost lines. */
export function PaymentMethodSelect({
  value,
  onChange,
  disabled = false,
  placeholder = "Select method (optional)",
  noneLabel = "— None —",
  allowNone = true,
  id,
  label = "Payment method",
  required = false,
  labelClassName = "text-xs text-muted-foreground",
  triggerClassName = "glass-input h-10",
  className,
}: SelectProps) {
  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor={id} className={labelClassName}>
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </Label>
      <Select
        value={value || (allowNone ? "__none__" : undefined)}
        onValueChange={(v) => onChange(v === "__none__" ? "" : (v as SalePaymentMethod))}
        disabled={disabled}
      >
        <SelectTrigger id={id} className={triggerClassName}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent position="popper">
          {allowNone ? <SelectItem value="__none__">{noneLabel}</SelectItem> : null}
          <SelectItem value="cash">Cash</SelectItem>
          <SelectItem value="online">Online</SelectItem>
          <SelectItem value="cheque">Cheque</SelectItem>
          <SelectItem value="pay_order">Pay Order</SelectItem>
          <SelectItem value="cash_deposit">Cash Deposit</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

type DetailProps = {
  method: PaymentMethodValue;
  bankId: string;
  transactionReferenceNo?: string;
  payOrderNo?: string;
  chequeNo?: string;
  depositSlipNo?: string;
  chequeDate?: string;
  banks?: BankOption[];
  /** @deprecated Cheque date is no longer shown in the UI. */
  showChequeDate?: boolean;
  /** When true, bank is rendered elsewhere (e.g. beside payment method). */
  hideBank?: boolean;
  required?: boolean;
  stacked?: boolean;
  onBankIdChange: (value: string) => void;
  onTransactionReferenceChange?: (value: string) => void;
  onPayOrderNoChange?: (value: string) => void;
  onChequeNoChange?: (value: string) => void;
  onDepositSlipNoChange?: (value: string) => void;
  onChequeDateChange?: (value: string) => void;
  disabled?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  className?: string;
};

function RequiredMark({ show }: { show?: boolean }) {
  return show ? <span className="text-destructive"> *</span> : null;
}

function detailFieldsLayoutClass(
  stacked: boolean,
  columns: 2 | 3 = 2,
  className?: string,
): string {
  if (stacked) {
    return cn("grid grid-cols-1 gap-4 w-full min-w-0", className);
  }
  if (columns === 3) {
    return cn("grid grid-cols-1 gap-4 items-end md:grid-cols-3", className);
  }
  return cn("grid grid-cols-1 gap-4 items-end md:grid-cols-2", className);
}

function BankField({
  bankId,
  banks,
  onBankIdChange,
  disabled,
  labelClassName,
  inputClassName,
  required,
}: {
  bankId: string;
  banks?: BankOption[];
  onBankIdChange: (value: string) => void;
  disabled?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  required?: boolean;
}) {
  return (
    <div className="space-y-1 relative z-30 min-w-0 w-full">
      <Label className={labelClassName}>
        Bank
        <RequiredMark show={required} />
      </Label>
      <ExpenseBankSelect
        value={bankId}
        banks={banks}
        onChange={onBankIdChange}
        disabled={disabled}
        placeholder="Select bank"
        triggerClassName={cn("w-full", inputClassName)}
      />
      {banks && banks.length === 0 ? (
        <p className="text-[11px] text-destructive">No banks found. Add a bank first.</p>
      ) : null}
    </div>
  );
}

/** Dynamic bank / reference fields for Online, Cheque, Pay Order, and Cash Deposit. */
export function PaymentMethodDetailFields({
  method,
  bankId,
  transactionReferenceNo = "",
  payOrderNo = "",
  chequeNo = "",
  depositSlipNo = "",
  banks,
  hideBank = false,
  required = false,
  onBankIdChange,
  onTransactionReferenceChange,
  onPayOrderNoChange,
  onChequeNoChange,
  onDepositSlipNoChange,
  disabled = false,
  labelClassName = "text-xs text-muted-foreground",
  inputClassName = "glass-input h-10",
  stacked = false,
  className,
}: DetailProps) {
  const expenseMeta = banks ? paymentMethodMeta(method) : null;
  const showBank = !hideBank;

  if (method === "online") {
    return (
      <div className={detailFieldsLayoutClass(stacked, 2, className)}>
        {showBank ? (
          <BankField
            bankId={bankId}
            banks={banks}
            onBankIdChange={onBankIdChange}
            disabled={disabled}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            required={required}
          />
        ) : null}
        <div className="space-y-1 min-w-0 w-full">
          <Label className={labelClassName}>
            {expenseMeta?.referenceLabel ?? "Transaction reference no."}
            <RequiredMark show={required && Boolean(expenseMeta?.referenceLabel)} />
          </Label>
          <Input
            className={cn("w-full", inputClassName)}
            value={transactionReferenceNo}
            onChange={(e) => onTransactionReferenceChange?.(e.target.value)}
            placeholder={expenseMeta?.referenceLabel ?? "Optional"}
            disabled={disabled}
          />
        </div>
      </div>
    );
  }

  if (method === "cheque") {
    return (
      <div className={detailFieldsLayoutClass(stacked, showBank ? 2 : 2, className)}>
        {showBank ? (
          <BankField
            bankId={bankId}
            banks={banks}
            onBankIdChange={onBankIdChange}
            disabled={disabled}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            required={required}
          />
        ) : null}
        <div className="space-y-1 min-w-0 w-full">
          <Label className={labelClassName}>
            Cheque number
            <RequiredMark show={required} />
          </Label>
          <Input
            className={cn("w-full", inputClassName)}
            value={chequeNo}
            onChange={(e) => onChequeNoChange?.(e.target.value)}
            placeholder="Cheque number"
            disabled={disabled}
          />
        </div>
      </div>
    );
  }

  if (method === "pay_order") {
    return (
      <div className={detailFieldsLayoutClass(stacked, 2, className)}>
        {showBank ? (
          <BankField
            bankId={bankId}
            banks={banks}
            onBankIdChange={onBankIdChange}
            disabled={disabled}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            required={required}
          />
        ) : null}
        <div className="space-y-1 min-w-0 w-full">
          <Label className={labelClassName}>
            {expenseMeta?.referenceLabel ?? "Pay order no."}
            <RequiredMark show={required} />
          </Label>
          <Input
            className={cn("w-full", inputClassName)}
            value={payOrderNo}
            onChange={(e) => onPayOrderNoChange?.(e.target.value)}
            placeholder={expenseMeta?.referenceLabel ?? "Optional"}
            disabled={disabled}
          />
        </div>
      </div>
    );
  }

  if (method === "cash_deposit") {
    return (
      <div className={detailFieldsLayoutClass(stacked, 2, className)}>
        {showBank ? (
          <BankField
            bankId={bankId}
            banks={banks}
            onBankIdChange={onBankIdChange}
            disabled={disabled}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            required={required}
          />
        ) : null}
        <div className="space-y-1 min-w-0 w-full">
          <Label className={labelClassName}>
            {expenseMeta?.referenceLabel ?? "Deposit slip no."}
            <RequiredMark show={required && Boolean(expenseMeta?.referenceLabel)} />
          </Label>
          <Input
            className={cn("w-full", inputClassName)}
            value={depositSlipNo}
            onChange={(e) => onDepositSlipNoChange?.(e.target.value)}
            placeholder={expenseMeta?.referenceLabel ?? "Optional"}
            disabled={disabled}
          />
        </div>
      </div>
    );
  }

  return null;
}

type GroupProps = {
  value: PaymentMethodDraft;
  onChange: (value: PaymentMethodDraft) => void;
  disabled?: boolean;
  allowNone?: boolean;
  required?: boolean;
  showSelect?: boolean;
  showDetails?: boolean;
  /** @deprecated Cheque date is no longer shown in the UI. */
  showChequeDate?: boolean;
  /** Put payment method + bank on one row; other detail fields on the next. */
  methodWithBankRow?: boolean;
  banks?: BankOption[];
  compact?: boolean;
  stacked?: boolean;
  id?: string;
  label?: string;
  labelClassName?: string;
  triggerClassName?: string;
  inputClassName?: string;
  selectClassName?: string;
  detailClassName?: string;
  className?: string;
};

/** Combined payment method selector + dynamic bank/reference fields. */
export function PaymentMethodFieldsGroup({
  value,
  onChange,
  disabled = false,
  allowNone = true,
  required = false,
  showSelect = true,
  showDetails = true,
  methodWithBankRow = false,
  banks,
  compact = false,
  stacked = false,
  id,
  label,
  labelClassName,
  triggerClassName,
  inputClassName,
  selectClassName,
  detailClassName,
  className,
}: GroupProps) {
  const resolvedLabelClass =
    labelClassName ?? (compact ? "text-xs text-muted-foreground" : "text-xs text-muted-foreground");
  const resolvedTriggerClass =
    triggerClassName ?? (compact ? "glass-input h-8 text-sm" : "glass-input h-10");
  const resolvedInputClass =
    inputClassName ?? (compact ? "glass-input h-8 text-sm" : "glass-input h-10");

  const patch = (partial: Partial<PaymentMethodDraft>) => onChange({ ...value, ...partial });
  const showBankBesideMethod =
    methodWithBankRow && !stacked && showSelect && paymentMethodUsesBank(value.paymentMethod);

  return (
    <div
      className={cn(
        stacked ? "flex w-full min-w-0 flex-col gap-4" : showSelect && showDetails ? "space-y-4" : undefined,
        className,
      )}
    >
      {showSelect ? (
        showBankBesideMethod ? (
          <div className="grid grid-cols-1 gap-4 items-end md:grid-cols-2 w-full min-w-0">
            <PaymentMethodSelect
              id={id}
              label={label}
              required={required}
              value={value.paymentMethod}
              onChange={(method) => onChange(applyPaymentMethodChange(value, method))}
              disabled={disabled}
              allowNone={allowNone}
              labelClassName={resolvedLabelClass}
              triggerClassName={resolvedTriggerClass}
              className={selectClassName}
            />
            <BankField
              bankId={value.bankId}
              banks={banks}
              onBankIdChange={(bankId) => patch({ bankId })}
              disabled={disabled}
              labelClassName={resolvedLabelClass}
              inputClassName={resolvedInputClass}
              required={required}
            />
          </div>
        ) : (
          <PaymentMethodSelect
            id={id}
            label={label}
            required={required}
            value={value.paymentMethod}
            onChange={(method) => onChange(applyPaymentMethodChange(value, method))}
            disabled={disabled}
            allowNone={allowNone}
            labelClassName={resolvedLabelClass}
            triggerClassName={resolvedTriggerClass}
            className={selectClassName}
          />
        )
      ) : null}

      {showDetails ? (
        <PaymentMethodDetailFields
          method={value.paymentMethod}
          bankId={value.bankId}
          transactionReferenceNo={value.transactionReferenceNo}
          payOrderNo={value.payOrderNo}
          chequeNo={value.chequeNo}
          depositSlipNo={value.depositSlipNo}
          banks={banks}
          hideBank={showBankBesideMethod}
          required={required}
          stacked={stacked}
          onBankIdChange={(bankId) => patch({ bankId })}
          onTransactionReferenceChange={(transactionReferenceNo) => patch({ transactionReferenceNo })}
          onPayOrderNoChange={(payOrderNo) => patch({ payOrderNo })}
          onChequeNoChange={(chequeNo) => patch({ chequeNo })}
          onDepositSlipNoChange={(depositSlipNo) => patch({ depositSlipNo })}
          disabled={disabled}
          labelClassName={resolvedLabelClass}
          inputClassName={resolvedInputClass}
          className={detailClassName}
        />
      ) : null}
    </div>
  );
}
